import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTimes, 
  faExclamationTriangle, 
  faExclamationCircle,
  faInfoCircle,
  faSpinner,
  faClock,
  faChartLine
} from '@fortawesome/free-solid-svg-icons';
import { cloudWatchService, CloudWatchAlert } from '../services/cloudWatchService';

export interface CloudWatchAlertsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const CloudWatchAlertsModal: React.FC<CloudWatchAlertsModalProps> = ({
  isOpen,
  onClose
}) => {
  const [alerts, setAlerts] = useState<CloudWatchAlert[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'high' | 'medium' | 'low'>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'ALARM' | 'OK' | 'INSUFFICIENT_DATA'>('all');

  useEffect(() => {
    if (isOpen) {
      fetchAlerts();
    }
  }, [isOpen]);

  const fetchAlerts = async () => {
    setLoading(true);
    setError(null);
    try {
      const alertData = await cloudWatchService.getAllAlerts();
      setAlerts(alertData.alerts);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch CloudWatch alerts');
    } finally {
      setLoading(false);
    }
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'high': return faExclamationTriangle;
      case 'medium': return faExclamationCircle;
      case 'low': return faInfoCircle;
      default: return faInfoCircle;
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'high': return 'text-red-600';
      case 'medium': return 'text-yellow-600';
      case 'low': return 'text-blue-600';
      default: return 'text-gray-600';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ALARM': return 'bg-red-100 text-red-800';
      case 'OK': return 'bg-green-100 text-green-800';
      case 'INSUFFICIENT_DATA': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredAlerts = alerts.filter(alert => {
    const severityMatch = filter === 'all' || alert.severity === filter;
    const statusMatch = statusFilter === 'all' || alert.status === statusFilter;
    return severityMatch && statusMatch;
  });

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString();
  };

  const formatDimensions = (dimensions: Array<{ Name: string; Value: string }>) => {
    return dimensions.map(dim => `${dim.Name}: ${dim.Value}`).join(', ');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-hidden">
      <div className="bg-white rounded shadow-xl max-w-7xl w-full max-h-[90vh] flex flex-col overflow-hidden font-mono">

        {/* Header */}
        <div className="flex items-center justify-between p-3 border-b border-gray-300">
          <div className="flex items-center space-x-3">
            <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-600 text-base" />
            <h2 className="text-base font-bold text-gray-900">CloudWatch Alerts</h2>
            {alerts.length > 0 && (
              <span className="bg-red-100 text-red-700 px-3 py-1 rounded-full text-sm">
                {filteredAlerts.length} of {alerts.length}
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <FontAwesomeIcon icon={faTimes} className="text-xl" />
          </button>
        </div>

        {/* Filters */}
        <div className="p-3 border-b border-gray-300 bg-gray-50">
          <div className="flex flex-wrap gap-4">
            <div className="flex items-center space-x-2">
              <label className="text-sm font-medium text-gray-700">Severity:</label>
              <select 
                value={filter} 
                onChange={(e) => setFilter(e.target.value as any)}
                className="text-sm border border-gray-300 rounded px-2 py-1"
              >
                <option value="all">All</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>
            <div className="flex items-center space-x-2">
              <label className="text-sm font-medium text-gray-700">Status:</label>
              <select 
                value={statusFilter} 
                onChange={(e) => setStatusFilter(e.target.value as any)}
                className="text-sm border border-gray-300 rounded px-2 py-1"
              >
                <option value="all">All</option>
                <option value="ALARM">Alarm</option>
                <option value="OK">OK</option>
                <option value="INSUFFICIENT_DATA">Insufficient Data</option>
              </select>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex flex-col">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <FontAwesomeIcon icon={faSpinner} className="text-2xl text-blue-600 animate-spin mb-2" />
                <p className="text-gray-600">Loading CloudWatch alerts...</p>
              </div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <p className="text-red-600 mb-2">Error: {error}</p>
                <button
                  onClick={fetchAlerts}
                  className="px-3 py-1.5 bg-blue-600 text-white rounded text-xs hover:bg-blue-700"
                >
                  Retry
                </button>
              </div>
            </div>
          ) : filteredAlerts.length === 0 ? (
            <div className="flex items-center justify-center h-64">
              <p className="text-gray-500">No alerts found matching the current filters</p>
            </div>
          ) : (
            <div className="flex-1 overflow-auto">
              <div className="p-3">
                <div className="space-y-3">
                  {filteredAlerts.map((alert, index) => (
                    <div key={alert.id || index} className="bg-white border border-gray-300 rounded p-3 hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start space-x-3 flex-1">
                          <FontAwesomeIcon 
                            icon={getSeverityIcon(alert.severity)} 
                            className={`mt-1 ${getSeverityColor(alert.severity)}`}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-3 mb-2">
                              <h3 className="font-bold text-gray-900 truncate text-sm">{alert.name}</h3>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(alert.status)}`}>
                                {alert.status}
                              </span>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                alert.severity === 'high' ? 'bg-red-50 text-red-700' :
                                alert.severity === 'medium' ? 'bg-yellow-50 text-yellow-700' :
                                'bg-blue-50 text-blue-700'
                              }`}>
                                {alert.severity.toUpperCase()}
                              </span>
                            </div>
                            
                            <p className="text-xs text-gray-600 mb-2">{alert.description}</p>

                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 text-xs">
                              <div>
                                <span className="font-medium text-gray-700">Metric:</span>
                                <div className="text-gray-600">{alert.metricName}</div>
                              </div>
                              <div>
                                <span className="font-medium text-gray-700">Namespace:</span>
                                <div className="text-gray-600">{alert.namespace}</div>
                              </div>
                              <div>
                                <span className="font-medium text-gray-700">Region:</span>
                                <div className="text-gray-600">{alert.region}</div>
                              </div>
                              <div>
                                <span className="font-medium text-gray-700">Threshold:</span>
                                <div className="text-gray-600">{alert.threshold} ({alert.comparisonOperator})</div>
                              </div>
                              <div>
                                <span className="font-medium text-gray-700">Period:</span>
                                <div className="text-gray-600">{alert.period}s</div>
                              </div>
                              <div>
                                <span className="font-medium text-gray-700">Statistic:</span>
                                <div className="text-gray-600">{alert.statistic}</div>
                              </div>
                            </div>
                            
                            {alert.metadata.dimensions.length > 0 && (
                              <div className="mt-2 text-xs">
                                <span className="font-bold text-gray-700">Dimensions:</span>
                                <div className="text-gray-600">{formatDimensions(alert.metadata.dimensions)}</div>
                              </div>
                            )}

                            <div className="mt-2 text-xs">
                              <span className="font-bold text-gray-700">Reason:</span>
                              <div className="text-gray-600">{alert.stateReason}</div>
                            </div>
                          </div>
                        </div>
                        
                        <div className="text-right text-sm text-gray-500 ml-4">
                          <FontAwesomeIcon icon={faClock} className="mr-1" />
                          {formatTimestamp(alert.stateChangeTime)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-3 border-t border-gray-300 bg-gray-50">
          <div className="text-xs text-gray-500">
            {filteredAlerts.length > 0 && `Showing ${filteredAlerts.length} alerts`}
          </div>
          <div className="flex space-x-2">
            <button
              onClick={fetchAlerts}
              disabled={loading}
              className="px-3 py-1.5 bg-blue-600 text-white rounded text-xs hover:bg-blue-700 disabled:opacity-50"
            >
              Refresh
            </button>
            <button
              onClick={onClose}
              className="px-3 py-1.5 bg-gray-300 text-gray-700 rounded text-xs hover:bg-gray-400"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};